import React, { useState, useEffect } from "react";
import api from "../services/api";

const money = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Separate Modal Component to prevent render-tree leaks
function ManualEntryModal({ accounts, onClose, onSaved }) {
  const [description, setDescription] = useState("");
  const [lines, setLines] = useState([
    { account: "", type: "debit", amount: "" },
    { account: "", type: "credit", amount: "" },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const totalDebit = lines
    .filter((l) => l.type === "debit")
    .reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const totalCredit = lines
    .filter((l) => l.type === "credit")
    .reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const isBalanced =
    Math.round(totalDebit * 100) === Math.round(totalCredit * 100) && totalDebit > 0;

  const updateLine = (index, field, value) => {
    const updated = [...lines];
    updated[index][field] = value;
    setLines(updated);
  };

  const addLine = () =>
    setLines([...lines, { account: "", type: "debit", amount: "" }]);
  const removeLine = (index) =>
    setLines(lines.filter((_, i) => i !== index));

  const handleSave = async () => {
    setError("");

    if (!description.trim()) {
      setError("Description is required");
      return;
    }
    if (!isBalanced) {
      setError(`Not balanced — debits ₹${totalDebit} ≠ credits ₹${totalCredit}`);
      return;
    }
    if (lines.some((l) => !l.account || !l.amount)) {
      setError("Every line needs an account and amount");
      return;
    }

    try {
      setSaving(true);
      await api.post("/accounting/journal/manual", {
        description: description.trim(),
        lines: lines.map((l) => ({
          account: l.account,
          type: l.type,
          amount: Number(l.amount),
        })),
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>Add Manual Journal Entry</h3>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Description</label>
          <input
            style={styles.input}
            placeholder="e.g. Marketing spend - Facebook Ads, September"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div style={styles.lineList}>
          {lines.map((line, i) => (
            <div key={i} style={styles.lineRow}>
              <select
                style={{ ...styles.select, flex: "2 1 180px" }}
                value={line.account}
                onChange={(e) => updateLine(i, "account", e.target.value)}
              >
                <option value="">Select account</option>
                {accounts.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>

              <select
                style={{ ...styles.select, flex: "1 1 100px" }}
                value={line.type}
                onChange={(e) => updateLine(i, "type", e.target.value)}
              >
                <option value="debit">Debit</option>
                <option value="credit">Credit</option>
              </select>

              <input
                style={{ ...styles.input, flex: "1 1 110px" }}
                type="number"
                placeholder="Amount"
                value={line.amount}
                onChange={(e) => updateLine(i, "amount", e.target.value)}
              />

              {lines.length > 2 && (
                <button
                  onClick={() => removeLine(i)}
                  style={styles.removeLineBtn}
                  title="Remove Line"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        <button onClick={addLine} style={styles.addLineBtn}>
          + Add another line
        </button>

        <div style={styles.balanceSummary(isBalanced)}>
          <span>Debits: {money(totalDebit)}</span>
          <span>Credits: {money(totalCredit)}</span>
          <span>{isBalanced ? "✓ Balanced" : "✗ Unbalanced"}</span>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        <div style={styles.modalFooter}>
          <button style={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            style={styles.saveBtn(saving || !isBalanced)}
            onClick={handleSave}
            disabled={saving || !isBalanced}
          >
            {saving ? "Saving..." : "Save Entry"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AccountingDashboard() {
  const [tab, setTab] = useState("trial");
  const [trialBalance, setTrialBalance] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  const [journalEntries, setJournalEntries] = useState([]);
  const [journalPage, setJournalPage] = useState(1);
  const [journalTotalPages, setJournalTotalPages] = useState(1);
  const [showManualForm, setShowManualForm] = useState(false);

  const loadJournal = async (page = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/journal", {
        params: { page, limit: 25 },
      });
      setJournalEntries(res.data.entries || []);
      setJournalTotalPages(res.data.totalPages || 1);
      setJournalPage(page);
    } finally {
      setLoading(false);
    }
  };

  const loadTrialBalance = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/trial-balance");
      setTrialBalance(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadPnl = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/profit-loss");
      setPnl(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadBalanceSheet = async () => {
    setLoading(true);
    try {
      const res = await api.get("/accounting/balance-sheet");
      setBalanceSheet(res.data);
    } finally {
      setLoading(false);
    }
  };

  const loadAccounts = async () => {
    const res = await api.get("/accounting/chart-of-accounts");
    setAccounts(res.data.accounts || []);
  };

  const loadLedger = async (account) => {
    if (!account) return;
    setLoading(true);
    try {
      const res = await api.get("/accounting/ledger", {
        params: { account },
      });
      setLedger(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (tab === "journal") loadJournal(1);
    if (tab === "trial") loadTrialBalance();
    if (tab === "pnl") loadPnl();
    if (tab === "balance") loadBalanceSheet();
  }, [tab]);

  return (
    <div style={styles.page}>
      <div style={styles.headerContainer}>
        <div>
          <h1 style={styles.title}>Accounting Overview</h1>
          <p style={styles.subtitle}>Manage ledgers, journal records, and financial balance reports.</p>
        </div>
      </div>

      <div style={styles.tabsWrapper}>
        {[
          { key: "journal", label: "Journal Entries" },
          { key: "ledger", label: "Ledger" },
          { key: "trial", label: "Trial Balance" },
          { key: "pnl", label: "Profit & Loss" },
          { key: "balance", label: "Balance Sheet" },
        ].map((t) => (
          <button
            key={t.key}
            style={{
              ...styles.tabBtn,
              ...(tab === t.key ? styles.tabActive : {}),
            }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={styles.loadingCard}>
          <div style={styles.spinner}></div>
          <span>Loading data...</span>
        </div>
      )}

      {/* JOURNAL ENTRIES TAB */}
      {tab === "journal" && !loading && (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.sectionTitle}>Journal Entries</h3>
            <button
              style={styles.addEntryBtn}
              onClick={() => setShowManualForm(true)}
            >
              + Add Manual Entry
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {journalEntries.map((e) => (
              <div key={e._id} style={styles.journalEntryCard}>
                <div style={styles.journalHeader}>
                  <span style={styles.journalDate}>
                    {new Date(e.date).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                  {e.isManual && <span style={styles.manualTag}>Manual</span>}
                </div>
                <div style={styles.journalDesc}>{e.description}</div>
                <div style={styles.tableResponsive}>
                  <table style={styles.table}>
                    <tbody>
                      {e.lines.map((l, i) => (
                        <tr key={i} style={styles.tableRow}>
                          <td style={styles.td}>{l.account}</td>
                          <td style={styles.tdRight}>
                            {l.type === "debit" ? money(l.amount) : "—"}
                          </td>
                          <td style={styles.tdRight}>
                            {l.type === "credit" ? money(l.amount) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div style={styles.paginationWrapper}>
            <button
              disabled={journalPage <= 1}
              onClick={() => loadJournal(journalPage - 1)}
              style={styles.pageBtn(journalPage <= 1)}
            >
              Prev
            </button>
            <span style={styles.pageInfo}>
              Page {journalPage} of {journalTotalPages}
            </span>
            <button
              disabled={journalPage >= journalTotalPages}
              onClick={() => loadJournal(journalPage + 1)}
              style={styles.pageBtn(journalPage >= journalTotalPages)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showManualForm && (
        <ManualEntryModal
          accounts={accounts}
          onClose={() => setShowManualForm(false)}
          onSaved={() => {
            setShowManualForm(false);
            loadJournal(1);
          }}
        />
      )}

      {/* TRIAL BALANCE */}
      {tab === "trial" && trialBalance && !loading && (
        <div style={styles.card}>
          {!trialBalance.balanced && (
            <div style={styles.errorBanner}>
              ⚠️ Trial balance does NOT tally — debits ({money(trialBalance.totalDebit)}) ≠ credits (
              {money(trialBalance.totalCredit)}).
            </div>
          )}
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Account</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.thRight}>Debit</th>
                  <th style={styles.thRight}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {trialBalance.rows.map((r) => (
                  <tr key={r.account} style={styles.tableRow}>
                    <td style={styles.tdBold}>{r.account}</td>
                    <td style={styles.tdMuted}>{r.accountType}</td>
                    <td style={styles.tdRight}>
                      {r.debit ? money(r.debit) : "—"}
                    </td>
                    <td style={styles.tdRight}>
                      {r.credit ? money(r.credit) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal} colSpan={2}>
                    Total
                  </td>
                  <td style={styles.tdTotalRight}>
                    {money(trialBalance.totalDebit)}
                  </td>
                  <td style={styles.tdTotalRight}>
                    {money(trialBalance.totalCredit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* PROFIT & LOSS */}
      {tab === "pnl" && pnl && !loading && (
        <div style={styles.card}>
          <h3 style={styles.sectionTitle}>Revenue</h3>
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <tbody>
                {pnl.revenue.map((r) => (
                  <tr key={r.account} style={styles.tableRow}>
                    <td style={styles.td}>{r.account}</td>
                    <td style={styles.tdRight}>{money(r.amount)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal}>Total Revenue</td>
                  <td style={styles.tdTotalRight}>{money(pnl.totalRevenue)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...styles.sectionTitle, marginTop: "24px" }}>Expenses</h3>
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <tbody>
                {pnl.expenses.map((r) => (
                  <tr key={r.account} style={styles.tableRow}>
                    <td style={styles.td}>{r.account}</td>
                    <td style={styles.tdRight}>{money(r.amount)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal}>Total Expenses</td>
                  <td style={styles.tdTotalRight}>{money(pnl.totalExpense)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={styles.netProfitBox(pnl.netProfit >= 0)}>
            <span>Net {pnl.netProfit >= 0 ? "Profit" : "Loss"}</span>
            <span>{money(Math.abs(pnl.netProfit))}</span>
          </div>
        </div>
      )}

      {/* BALANCE SHEET */}
      {tab === "balance" && balanceSheet && !loading && (
        <div style={styles.card}>
          {!balanceSheet.balanced && (
            <div style={styles.errorBanner}>
              ⚠️ Balance sheet does NOT balance — Assets ({money(balanceSheet.totalAssets)}) ≠
              Liabilities + Equity ({money(balanceSheet.totalLiabilitiesAndEquity)}).
            </div>
          )}

          <h3 style={styles.sectionTitle}>Assets</h3>
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <tbody>
                {balanceSheet.assets.map((a) => (
                  <tr key={a.account} style={styles.tableRow}>
                    <td style={styles.td}>{a.account}</td>
                    <td style={styles.tdRight}>{money(a.amount)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal}>Total Assets</td>
                  <td style={styles.tdTotalRight}>
                    {money(balanceSheet.totalAssets)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...styles.sectionTitle, marginTop: "24px" }}>
            Liabilities
          </h3>
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <tbody>
                {balanceSheet.liabilities.map((l) => (
                  <tr key={l.account} style={styles.tableRow}>
                    <td style={styles.td}>{l.account}</td>
                    <td style={styles.tdRight}>{money(l.amount)}</td>
                  </tr>
                ))}
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal}>Total Liabilities</td>
                  <td style={styles.tdTotalRight}>
                    {money(balanceSheet.totalLiabilities)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ ...styles.sectionTitle, marginTop: "24px" }}>Equity</h3>
          <div style={styles.tableResponsive}>
            <table style={styles.table}>
              <tbody>
                <tr style={styles.tableRow}>
                  <td style={styles.td}>Retained Earnings</td>
                  <td style={styles.tdRight}>
                    {money(balanceSheet.equity.retainedEarnings)}
                  </td>
                </tr>
                <tr style={styles.totalRow}>
                  <td style={styles.tdTotal}>Total Liabilities + Equity</td>
                  <td style={styles.tdTotalRight}>
                    {money(balanceSheet.totalLiabilitiesAndEquity)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* LEDGER */}
      {tab === "ledger" && (
        <div style={styles.card}>
          <div style={{ marginBottom: "16px" }}>
            <label style={styles.label}>Select Account</label>
            <select
              style={{ ...styles.select, maxWidth: "320px", width: "100%" }}
              value={selectedAccount}
              onChange={(e) => {
                setSelectedAccount(e.target.value);
                loadLedger(e.target.value);
              }}
            >
              <option value="">-- Choose Account --</option>
              {accounts.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          {ledger && !loading && (
            <div style={styles.tableResponsive}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Description</th>
                    <th style={styles.thRight}>Debit</th>
                    <th style={styles.thRight}>Credit</th>
                    <th style={styles.thRight}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.rows.map((r, i) => (
                    <tr key={i} style={styles.tableRow}>
                      <td style={styles.td}>
                        {new Date(r.date).toLocaleDateString("en-IN")}
                      </td>
                      <td style={styles.tdMuted}>{r.description}</td>
                      <td style={styles.tdRight}>
                        {r.debit ? money(r.debit) : "—"}
                      </td>
                      <td style={styles.tdRight}>
                        {r.credit ? money(r.credit) : "—"}
                      </td>
                      <td style={styles.tdRightBold}>
                        {money(r.runningBalance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={styles.totalRow}>
                    <td style={styles.tdTotal} colSpan={4}>
                      Closing Balance
                    </td>
                    <td style={styles.tdTotalRight}>
                      {money(ledger.closingBalance)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Styling Object
const styles = {
  page: {
    padding: "24px 16px",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    boxSizing: "border-box",
  },
  headerContainer: {
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    margin: "4px 0 0 0",
    fontSize: "14px",
    color: "#64748b",
  },
  tabsWrapper: {
    display: "flex",
    gap: "8px",
    marginBottom: "20px",
    overflowX: "auto",
    paddingBottom: "4px",
    WebkitOverflowScrolling: "touch",
  },
  tabBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    color: "#475569",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  tabActive: {
    background: "#4f46e5",
    color: "#ffffff",
    borderColor: "#4f46e5",
    boxShadow: "0 2px 4px rgba(79, 70, 229, 0.2)",
  },
  card: {
    background: "#ffffff",
    borderRadius: "12px",
    padding: "20px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginBottom: "16px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#0f172a",
    margin: 0,
  },
  tableResponsive: {
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "500px",
  },
  th: {
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  thRight: {
    textAlign: "right",
    padding: "10px 12px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    textTransform: "uppercase",
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  tableRow: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "12px",
    fontSize: "13px",
    color: "#334155",
  },
  tdBold: {
    padding: "12px",
    fontSize: "13px",
    color: "#0f172a",
    fontWeight: "600",
  },
  tdMuted: {
    padding: "12px",
    fontSize: "13px",
    color: "#64748b",
  },
  tdRight: {
    padding: "12px",
    fontSize: "13px",
    color: "#334155",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  tdRightBold: {
    padding: "12px",
    fontSize: "13px",
    color: "#0f172a",
    fontWeight: "600",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  totalRow: {
    background: "#f8fafc",
    borderTop: "2px solid #e2e8f0",
  },
  tdTotal: {
    padding: "12px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#0f172a",
  },
  tdTotalRight: {
    padding: "12px",
    fontSize: "13px",
    fontWeight: "700",
    color: "#0f172a",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },
  addEntryBtn: {
    padding: "8px 14px",
    borderRadius: "6px",
    border: "none",
    background: "#4f46e5",
    color: "#fff",
    fontWeight: "600",
    fontSize: "13px",
    cursor: "pointer",
  },
  journalEntryCard: {
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    padding: "14px",
    background: "#ffffff",
  },
  journalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
  },
  journalDate: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "500",
  },
  manualTag: {
    background: "#fef3c7",
    color: "#b45309",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "600",
  },
  journalDesc: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: "10px",
  },
  paginationWrapper: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    justifyContent: "center",
    alignItems: "center",
  },
  pageBtn: (disabled) => ({
    padding: "6px 14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    background: disabled ? "#f1f5f9" : "#ffffff",
    color: disabled ? "#94a3b8" : "#334155",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: "13px",
    fontWeight: "500",
  }),
  pageInfo: {
    fontSize: "13px",
    color: "#64748b",
  },
  errorBanner: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    padding: "12px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "16px",
  },
  netProfitBox: (positive) => ({
    marginTop: "20px",
    padding: "16px",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "15px",
    display: "flex",
    justifyContent: "space-between",
    background: positive ? "#f0fdf4" : "#fef2f2",
    color: positive ? "#15803d" : "#b91c1c",
    border: `1px solid ${positive ? "#bbf7d0" : "#fecaca"}`,
  }),
  loadingCard: {
    padding: "40px",
    textAlign: "center",
    color: "#64748b",
    background: "#ffffff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 1000,
  },
  modalContent: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "560px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "20px",
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
  },
  modalTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
  },
  closeBtn: {
    border: "none",
    background: "transparent",
    fontSize: "16px",
    cursor: "pointer",
    color: "#64748b",
  },
  formGroup: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    boxSizing: "border-box",
  },
  select: {
    padding: "8px 12px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    fontSize: "13px",
    background: "#fff",
    boxSizing: "border-box",
  },
  lineList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "12px",
  },
  lineRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  removeLineBtn: {
    background: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    padding: "8px 10px",
    cursor: "pointer",
  },
  addLineBtn: {
    background: "transparent",
    border: "1px dashed #cbd5e1",
    borderRadius: "6px",
    padding: "8px",
    color: "#4f46e5",
    fontWeight: "600",
    fontSize: "13px",
    cursor: "pointer",
    width: "100%",
  },
  balanceSummary: (isBalanced) => ({
    marginTop: "16px",
    padding: "10px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    display: "flex",
    justifyContent: "space-between",
    background: isBalanced ? "#f0fdf4" : "#fef2f2",
    color: isBalanced ? "#15803d" : "#b91c1c",
  }),
  modalFooter: {
    display: "flex",
    gap: "10px",
    marginTop: "20px",
    justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: "8px 14px",
    borderRadius: "6px",
    border: "1px solid #cbd5e1",
    background: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    cursor: "pointer",
  },
  saveBtn: (disabled) => ({
    padding: "8px 16px",
    borderRadius: "6px",
    border: "none",
    background: disabled ? "#a5b4fc" : "#4f46e5",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    cursor: disabled ? "not-allowed" : "pointer",
  }),
};

export default AccountingDashboard;